import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft } from 'lucide-react';
import { ClientPicker } from '../components/ClientPicker';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function NewMeeting() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [clientId, setClientId] = useState(searchParams.get('client') || '');
  const [title, setTitle] = useState('');
  const [meetingType, setMeetingType] = useState('');
  const [date, setDate] = useState('');
  const [time, setTime] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleClientChange = (id, _name) => {
    setClientId(id);
    // name is optional and can be ignored in this component
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    if (!clientId) {
      setError('Please select a client');
      setLoading(false);
      return;
    }

    try {
      await axios.post(
        `${API}/meetings`,
        {
          client_id: clientId,
          title,
          meeting_type: meetingType,
          date,
          time: time || undefined,
          status: 'scheduled',
          booked_by_client: false,
        },
        { withCredentials: true }
      );
      navigate(`/clients/${clientId}`);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create meeting');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background pb-8">
      <div className="max-w-2xl mx-auto p-4 sm:p-6">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-text-secondary hover:text-foreground mb-4 transition-colors"
          data-testid="back-button"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>Back</span>
        </button>

        <h1 className="text-3xl font-serif font-bold text-foreground mb-6" data-testid="new-meeting-title">
          New Meeting
        </h1>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">
              Client *
            </label>
            <ClientPicker value={clientId} onChange={handleClientChange} error={error && !clientId ? error : ''} />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">
              Title
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
              className="w-full bg-surface-raised border border-border rounded-lg px-4 py-2 text-foreground placeholder-text-muted focus:border-primary focus:ring-1 focus:ring-primary outline-none transition-all"
              placeholder="Meeting title"
              data-testid="meeting-title-input"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">
              Meeting Type *
            </label>
            <select
              value={meetingType}
              onChange={(e) => setMeetingType(e.target.value)}
              required
              className="w-full bg-surface-raised border border-border rounded-lg px-4 py-2 text-foreground focus:border-primary focus:ring-1 focus:ring-primary outline-none transition-all"
              data-testid="meeting-type-select"
            >
              <option value="">Select type...</option>
              <option value="consultation">Consultation</option>
              <option value="shoot">Shoot</option>
              <option value="review">Review</option>
              <option value="planning">Planning</option>
              <option value="delivery">Delivery</option>
              <option value="other">Other</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">
              Date *
            </label>
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              required
              className="w-full bg-surface-raised border border-border rounded-lg px-4 py-2 text-foreground focus:border-primary focus:ring-1 focus:ring-primary outline-none transition-all"
              data-testid="meeting-date-input"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">
              Time
            </label>
            <input
              type="time"
              value={time}
              onChange={(e) => setTime(e.target.value)}
              className="w-full bg-surface-raised border border-border rounded-lg px-4 py-2 text-foreground focus:border-primary focus:ring-1 focus:ring-primary outline-none transition-all"
              data-testid="meeting-time-input"
            />
          </div>

          {error && !(!clientId) && (
            <div className="text-alert text-sm bg-alert/10 border border-alert/20 rounded-lg p-3">
              {error}
            </div>
          )}

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="flex-1 bg-secondary text-foreground hover:bg-secondary/80 border border-border rounded-lg px-4 py-2 font-medium transition-colors"
              data-testid="cancel-button"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 bg-primary text-background hover:bg-primary/90 rounded-lg px-4 py-2 font-medium transition-colors disabled:opacity-50"
              data-testid="submit-button"
            >
              {loading ? 'Creating...' : 'Create Meeting'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
